import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GetCommunityPostRepliesDocument,
  GetCommunityPostRepliesQuery,
  GetCommunityPostRepliesQueryVariables,
  useGetCommunityPostRepliesQuery,
} from 'generated/graphql';
import { useAsyncQueryRaw } from '@client/new/helpers/gql';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import { toAuthorId } from '../model/post.types';
import { Reply, ReplyId, toReplyId } from '../model/reply.types';
import { CommunityId } from '../model/community.types';
import { isLevelOneReply } from '../lib/groupReplies';

type ServerPost = NonNullable<GetCommunityPostRepliesQuery['post']>;
type ServerReply = ServerPost['replies'][number];

const REPLIES_PAGE_SIZE = 25;
const REPLIES_POLL_INTERVAL_MS = 30_000;
const OLDER_PAGE_DIRECTION = 'olderThan';

export type CommunityRepliesResult = {
  replies: Async<Reply[]>;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

type OlderPage = { cursor: ReplyId; replies: Reply[] };

const toReply = (raw: ServerReply): Reply => ({
  id: toReplyId(raw.id),
  parentId: raw.parentId ?? null,
  author: {
    id: toAuthorId(raw.author.id),
    name: raw.author.username,
    avatarUrl: raw.author.profilePicture,
    email: raw.author.email || null,
    marketing: raw.author.marketing ?? null,
    isOwn: (raw.author.badges ?? []).includes('own'),
    isCreator: (raw.author.badges ?? []).includes('creator'),
    canReceiveDm: raw.author.hasDirectMessagesAccess === true,
  },
  createdAt: new Date(raw.createdAt),
  content: raw.text,
  edited: raw.edited,
  reactions: raw.reactions.map((reaction) => ({
    type: reaction.type,
    count: reaction.count,
    isToggled: reaction.isToggled,
  })),
  isSensitive: raw.isSensitiveContent,
});

const toReplies = (data: GetCommunityPostRepliesQuery): Reply[] =>
  data.post ? data.post.replies.map(toReply) : [];

const filterLevelOne = (replies: Reply[], postId: string): Reply[] =>
  replies.filter((reply) => isLevelOneReply(reply, postId));

const dedupeById = (replies: Reply[]): Reply[] => {
  const seen = new Set<ReplyId>();
  const result: Reply[] = [];
  for (const reply of replies) {
    if (seen.has(reply.id)) continue;
    seen.add(reply.id);
    result.push(reply);
  }
  return result;
};

export const useCommunityPostReplies = (
  communityId: CommunityId,
  postId: string,
  canModerate: boolean,
): CommunityRepliesResult => {
  const client = useApolloClient();
  const query = useGetCommunityPostRepliesQuery({
    variables: { communityId, postId, canModerate },
    fetchPolicy: 'cache-and-network',
    pollInterval: REPLIES_POLL_INTERVAL_MS,
  });

  const { data: firstPage } = useAsyncQueryRaw({ query, mapper: toReplies });

  // Older pages are normalized into the cache under their own
  // replies(direction, lastId) field entries and mirrored into hook state
  // through cache-only watchers, so optimistic reply updates (delete,
  // reactions) reach them; the 30s first-page poll rewrites only the
  // argument-less replies entry and cannot clobber them.
  const [olderPages, setOlderPages] = useState<OlderPage[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const unsubscribersRef = useRef<Array<() => void>>([]);
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    setOlderPages([]);
    setIsLoadingMore(false);
    setReachedEnd(false);
    const unsubscribers = unsubscribersRef.current;
    return () => {
      unsubscribers.splice(0).forEach((unsubscribe) => unsubscribe());
    };
  }, [communityId, postId, canModerate]);

  const serverReplies = firstPage.type === 'fetched' ? firstPage.value : [];
  const combinedNewestFirst = dedupeById([
    ...serverReplies,
    ...olderPages.flatMap((page) => page.replies),
  ]);

  const replies: Async<Reply[]> =
    firstPage.type === 'fetched'
      ? toAsyncFetched([...combinedNewestFirst].reverse())
      : firstPage;

  const hasMore =
    firstPage.type === 'fetched' &&
    !reachedEnd &&
    filterLevelOne(serverReplies, postId).length >= REPLIES_PAGE_SIZE;

  const loadMore = useCallback(() => {
    if (isLoadingMore || reachedEnd) return;
    if (firstPage.type !== 'fetched') return;
    const combined = dedupeById([
      ...firstPage.value,
      ...olderPages.flatMap((page) => page.replies),
    ]);
    const levelOne = filterLevelOne(combined, postId);
    if (levelOne.length === 0) return;
    const cursor = levelOne[levelOne.length - 1].id;
    const variables: GetCommunityPostRepliesQueryVariables = {
      communityId,
      postId,
      canModerate,
      direction: OLDER_PAGE_DIRECTION,
      lastId: cursor,
    };
    const generation = generationRef.current;

    setIsLoadingMore(true);
    client
      .query<
        GetCommunityPostRepliesQuery,
        GetCommunityPostRepliesQueryVariables
      >({
        query: GetCommunityPostRepliesDocument,
        variables,
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (generationRef.current !== generation) return;
        const page = toReplies(data);
        if (filterLevelOne(page, postId).length < REPLIES_PAGE_SIZE) {
          setReachedEnd(true);
        }
        if (page.length === 0) return;
        setOlderPages((prev) => [...prev, { cursor, replies: page }]);
        const watcher = client.watchQuery<
          GetCommunityPostRepliesQuery,
          GetCommunityPostRepliesQueryVariables
        >({
          query: GetCommunityPostRepliesDocument,
          variables,
          fetchPolicy: 'cache-only',
        });
        const subscription = watcher.subscribe((result) => {
          if (generationRef.current !== generation) return;
          if (!result.data?.post) return;
          const replies = toReplies(result.data);
          setOlderPages((prev) =>
            prev.map((page) =>
              page.cursor === cursor ? { cursor, replies } : page,
            ),
          );
        });
        unsubscribersRef.current.push(() => subscription.unsubscribe());
      })
      .catch(() => setReachedEnd(true))
      .finally(() => setIsLoadingMore(false));
  }, [
    client,
    communityId,
    postId,
    canModerate,
    isLoadingMore,
    reachedEnd,
    firstPage,
    olderPages,
  ]);

  return { replies, hasMore, isLoadingMore, loadMore };
};
